import type { ChangeEvent, ChangeEventHandler, Ref } from 'react';
import React, { forwardRef, useMemo } from 'react';
import Tooltip from '../../utils/tooltip';
import { PROJECT_OPTION } from '../reactForm/fields/ChoiceField';
import { useSharedFormContext } from '../reactForm/utils';
import type { SelectProps } from './Select';
import Select from './Select';

interface OverridableSelectProps extends SelectProps {
    overridable: boolean;
    overridden: boolean;
    overrideName: string;
    onChange: ChangeEventHandler<HTMLSelectElement>;
}

function OverridableSelectInner(
    {
        data: incomingData,
        value: incomingValue,
        onChange: incomingOnChange,
        overridable,
        overridden,
        overrideName,
        ...props
    }: OverridableSelectProps,
    ref: Ref<HTMLSelectElement>
) {
    const { formController } = useSharedFormContext();

    const data = useMemo(() => {
        if (overridable && incomingValue) {
            if (!incomingData) {
                return incomingData;
            }

            const translation = incomingData.find(([key]) => key === incomingValue)?.[1];
            const projectOption: [string, string] = [
                PROJECT_OPTION,
                // Only show the project value in the option text when this select is not
                // overridden. Since this OverridableSelect is used in RowManagers, it's
                // difficult to determine the exact parent value here. Therefore, we choose
                // the simple approach and just don't show it.
                [...(!overridden ? [translation, ' - '] : []), gettext('project setting')].join(''),
            ];

            return [...incomingData, projectOption];
        }

        return incomingData;
    }, [overridable, overridden, incomingValue, incomingData]);

    const value = useMemo(() => {
        if (overridable && !overridden) {
            return PROJECT_OPTION;
        }

        return incomingValue;
    }, [incomingValue, overridable, overridden]);

    function onChange(e: ChangeEvent<HTMLSelectElement>) {
        const { value: v } = e.target;
        if (overridden && v === PROJECT_OPTION) {
            formController.setOverriddenField(overrideName, false);
            return false;
        }

        if (!overridden) {
            formController.setOverriddenField(overrideName, true, true);
        }

        return incomingOnChange(e);
    }

    return (
        <Tooltip
            content={gettext('Note: changing this value will override the project settings.')}
            disabled={!overridable || overridden}
        >
            <Select {...props} data={data} value={value} onChange={onChange} ref={ref} />
        </Tooltip>
    );
}

export const OverridableSelect = forwardRef<HTMLSelectElement, OverridableSelectProps>(
    OverridableSelectInner
);

OverridableSelect.displayName = 'OverridableSelect';
